import React, { useCallback, useState, useEffect, useRef, useMemo } from 'react';
import PropTypes from 'prop-types';
import { Box } from '@mui/material';
import { getSectionDescription, normalizeSectionName } from '../../../utils/sectionColors';
import { getTaskStatus } from '../../../utils/taskStatusIndicator';
import { getIconComponent } from './iconUtils';

// Import custom hooks
import useProjectTasks from '../../../hooks/useProjectTasks';
import useTaskMenu from '../../../hooks/useTaskMenu';
import useTaskMutations from '../../../hooks/useTaskMutations';
import useSectionTasks from '../../../hooks/useSectionTasks';
import useSection from '../../../hooks/useSection';
import useProjectSections from '../../../hooks/useProjectSections';
import useAuth from '../../../hooks/useAuth';

// Import validation
import validationService from '../../../services/validationService';
import projectService from '../../../services/projectService';
import ValidationDialog from '../../validation/ValidationDialog';
import ValidatorAssignmentDialog from '../../validation/ValidatorAssignmentDialog';
import ValidationHistoryPanel from '../../validation/ValidationHistoryPanel';

// Import components
import LoadingView from './LoadingView';
import ErrorView from './ErrorView';
import EmptyStateView from './EmptyStateView';
import SectionHeader from './SectionHeader';
import TasksGrid, { getTilePermission } from './TasksGrid';
import TaskMenu from './TaskMenu';
import AgentDialog from '../AgentDialog';
import FieldValidationDialog from '../FieldValidationDialog';
import AutomatedGenerationSection from './AutomatedGenerationSection';
import AgenticCrewWorkflow from '../AgenticCrewWorkflow';

/**
 * Foundation Component
 * Shows the foundation project tasks as a grid of tiles organized by section
 * 
 * @param {Object} props - Component props
 * @param {string} props.projectId - The project ID
 * @param {Object} props.project - The project data
 * @param {Function} props.showNotification - Function to show notifications
 * @param {Function} props.onOpenTaskSelector - Function to open task selector dialog
 * @returns {React.ReactElement} Foundation component
 */
// Lazy-load SearchDialog
const SearchDialog = React.lazy(() => import('../../common/SearchDialog'));

const Foundation = ({ projectId, project: propProject, showNotification, onOpenTaskSelector }) => {
  // Global search state
  const [searchOpen, setSearchOpen] = useState(false);

  // Cmd+K keyboard shortcut for search
  useEffect(() => {
    const handleKeyDown = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setSearchOpen(prev => !prev);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  // State for orchestrator dialog
  const [showOrchestratorDialog, setShowOrchestratorDialog] = useState(false);
  const [selectedTaskForOrchestrator, setSelectedTaskForOrchestrator] = useState(null);
  const [showFieldValidationDialog, setShowFieldValidationDialog] = useState(false);
  const [selectedTaskForFieldValidation, setSelectedTaskForFieldValidation] = useState(null);

  // Validation state
  const [validationSummary, setValidationSummary] = useState({});
  const [showValidationDialog, setShowValidationDialog] = useState(false);
  const [showAssignValidatorsDialog, setShowAssignValidatorsDialog] = useState(false);
  const [assignValidatorRestriction, setAssignValidatorRestriction] = useState(null); // null = both, 'internal', 'external'
  const [showValidationHistory, setShowValidationHistory] = useState(false);
  const [validationTargetTile, setValidationTargetTile] = useState(null);
  const [validationLoading, setValidationLoading] = useState(false);
  const [projectMembers, setProjectMembers] = useState([]);

  // State for content height
  const [contentHeight, setContentHeight] = useState('auto');
  // Refs for height calculation
  const containerRef = useRef(null);

  // Calculate dynamic content height based on window size and header heights
  useEffect(() => {
    const calculateContentHeight = () => {
      // Default header heights
      const mainHeaderHeight = 73;  // Main app header
      const foundationHeaderHeight = 80; // Foundation header including padding
      const additionalPadding = 100;  // Extra padding for margins, etc.

      // Calculate available height for content
      const windowHeight = window.innerHeight;
      const availableHeight = windowHeight - mainHeaderHeight - foundationHeaderHeight - additionalPadding;

      // Set minimum height to avoid too small containers
      const finalHeight = Math.max(availableHeight, 400);
      setContentHeight(`${finalHeight}px`);
    };

    // Initial calculation
    calculateContentHeight();

    // Re-calculate on window resize
    window.addEventListener('resize', calculateContentHeight);

    // Cleanup
    return () => window.removeEventListener('resize', calculateContentHeight);
  }, []);

  // Get project data and operations
  const {
    project,
    setLocalProject,
    refreshProject,
    updateProject,
    toggleTaskStatus,
    isUpdatingProject,
    isTogglingTaskStatus
  } = useProjectTasks({
    projectId,
    initialProject: propProject
  });

  // Get sections and their tasks from backend (with consolidated data)
  const {
    sections: consolidatedSections,
    isLoadingSections: isLoadingConsolidated,
    sectionsError: consolidatedError,
    refetchSections: refetchConsolidated,
    hasConsolidatedData,
    needsFallback
  } = useProjectSections({ 
    projectId, 
    activeOnly: true,
    enabled: !!projectId
  });

  // Fallback to regular sections if consolidated API isn't available
  const {
    sections: fallbackSections,
    isLoadingSections: isLoadingFallback,
    sectionsError: fallbackError,
    refetchSections: refetchFallback
  } = useSection({ 
    activeOnly: true
  });

  // Use consolidated data if available, otherwise use fallback
  const sections = hasConsolidatedData ? consolidatedSections : fallbackSections;
  const isLoadingSections = hasConsolidatedData ? isLoadingConsolidated : isLoadingFallback;
  const sectionsError = hasConsolidatedData ? consolidatedError : fallbackError;
  const refetchSections = hasConsolidatedData ? refetchConsolidated : refetchFallback;

  // Debug logging
  useEffect(() => {
    if (hasConsolidatedData) {
      console.log('🚀 Foundation: Using CONSOLIDATED API - Single call for all section data!');
      console.log('📊 Consolidated sections:', consolidatedSections?.length || 0);
    } else if (needsFallback) {
      console.log('⚠️ Foundation: Falling back to individual API calls');
    } else {
      console.log('🔄 Foundation: Using regular sections API');
    }
  }, [hasConsolidatedData, needsFallback, consolidatedSections]);

  // Get current user information
  const { user, hasPermission } = useAuth();

  // Fetch validation summary and project members
  const fetchValidationSummary = useCallback(async () => {
    if (!projectId) return;
    try {
      const summary = await validationService.getValidationSummary(projectId);
      setValidationSummary(summary || {});
    } catch (err) {
      // Silent fail - validation is supplementary
    }
  }, [projectId]);

  const fetchProjectMembers = useCallback(async () => {
    if (!projectId) return;
    try {
      const response = await projectService.getProjectMembers(projectId);
      const members = Array.isArray(response?.data) ? response.data : Array.isArray(response) ? response : [];
      setProjectMembers(members);
    } catch (err) {
      // Silent fail
    }
  }, [projectId]);

  useEffect(() => {
    fetchValidationSummary();
    fetchProjectMembers();
  }, [fetchValidationSummary, fetchProjectMembers]);

  // Check if current user is a validator for a given task
  const isUserValidator = useCallback((taskId) => {
    const taskValidation = validationSummary[taskId];
    if (!taskValidation?.validators) return false;
    const userId = user?.id || user?._id;
    const allValidators = [
      ...(taskValidation.validators.internal || []),
      ...(taskValidation.validators.external || [])
    ];
    return allValidators.some(v => v.user_id === userId);
  }, [validationSummary, user]);

  // Get validator type for current user on a task
  const getUserValidatorType = useCallback((taskId) => {
    const taskValidation = validationSummary[taskId];
    if (!taskValidation?.validators) return null;
    const userId = user?.id || user?._id;
    if (taskValidation.validators.internal?.some(v => v.user_id === userId)) return 'internal';
    if (taskValidation.validators.external?.some(v => v.user_id === userId)) return 'external';
    return null;
  }, [validationSummary, user]);

  // Check if user can assign validators (based on can_assign_validators flag or creator)
  const canAssignValidators = useMemo(() => {
    if (!user || !project) return false;
    const userId = user.id || user._id;

    // Creator always can
    const createdById = project.created_by?.id || project.created_by?._id || project.created_by;
    if (userId && createdById && String(userId) === String(createdById)) return true;

    // System admin always can
    if (user.role === 'admin') return true;

    // Check can_assign_validators flag from project members
    const members = projectMembers || [];
    const currentMember = members.find(m => String(m.user_id) === String(userId));
    if (currentMember?.can_assign_validators) return true;

    return false;
  }, [user, project, projectMembers]);

  // Validation handlers
  const handleValidate = useCallback((tile) => {
    setValidationTargetTile(tile);
    setShowValidationDialog(true);
  }, []);

  const handleAssignValidators = useCallback((tile, restrictTo = null) => {
    setValidationTargetTile(tile);
    setAssignValidatorRestriction(restrictTo);
    setShowAssignValidatorsDialog(true);
  }, []);

  const [historyRestriction, setHistoryRestriction] = useState(null);
  const [historyFetchKey, setHistoryFetchKey] = useState(0);
  const handleViewHistory = useCallback((tile, restrictTo = 'both') => {
    setValidationTargetTile(tile);
    setHistoryRestriction(restrictTo);
    setShowValidationHistory(true);
    setHistoryFetchKey(prev => prev + 1);
  }, []);

  // Direct history handler for TaskTile strip (always shows all history)
  const handleTileViewHistory = useCallback((tile) => {
    handleViewHistory(tile, 'both');
  }, [handleViewHistory]);

  const handleSubmitValidation = useCallback(async ({ decision, comment }) => {
    if (!validationTargetTile || !projectId) return;
    setValidationLoading(true);
    try {
      await validationService.submitValidation(projectId, validationTargetTile.id, { decision, comment });
      setShowValidationDialog(false);
      setValidationTargetTile(null);
      fetchValidationSummary();
      showNotification({ message: decision === 'approved' ? 'Task validated successfully' : 'Changes requested', type: 'success' });
    } catch (err) {
      showNotification({ message: err.detail || 'Failed to submit validation', type: 'error' });
    }
    setValidationLoading(false);
  }, [validationTargetTile, projectId, fetchValidationSummary, showNotification]);

  // Process tasks from sections
  const {
    filteredTiles: originalFilteredTiles,
    groupedTiles: originalGroupedTiles,
    conversations,
    isLoadingConversations
  } = useSectionTasks({
    project,
    sections,
    isLoadingSections,
    user
  });

  // Add feature-info tile for new-feature and change-request projects
  // Dependency on project.completedTasks ensures re-render when tasks are marked as done
  const filteredTiles = useMemo(() => {
    if (project?.type === 'new-feature') {
      // Check if feature-info tile already exists
      const hasFeatureInfo = originalFilteredTiles.some(tile =>
        tile.id === 'feature-info' || tile._id === 'feature-info'
      );

      if (!hasFeatureInfo) {
        // Check if feature-info is in completedTasks
        const isCompleted = project?.completedTasks?.includes('feature-info');

        // Add feature-info tile at the beginning
        const featureInfoTile = {
          id: 'feature-info',
          _id: 'feature-info',
          task_id: 'feature-info',
          title: 'Feature Info',
          icon: getIconComponent('FolderIcon'),
          description: 'Define feature information, requirements, and core details',
          status: isCompleted ? 'completed' : 'in_progress',  // Dynamic status based on completedTasks
          sectionName: 'Analysis',  // Add to Analysis section with User roles, User stories, Process flows
          sectionId: 'analysis-section',
          backgroundColor: '#E8F5E9',  // Green background
          iconColor: '#475969'  // Dark gray icon to match other tiles
        };

        return [featureInfoTile, ...originalFilteredTiles];
      }
    } else if (project?.type === 'change-request') {
      // Check if change-request-info tile already exists
      const hasChangeRequestInfo = originalFilteredTiles.some(tile =>
        tile.id === 'change-request-info' || tile._id === 'change-request-info'
      );

      if (!hasChangeRequestInfo) {
        // Check if change-request-info is in completedTasks
        const isCompleted = project?.completedTasks?.includes('change-request-info');

        // Add change-request-info tile at the beginning
        const changeRequestInfoTile = {
          id: 'change-request-info',
          _id: 'change-request-info',
          task_id: 'change-request-info',
          title: 'Change Request Info',
          icon: getIconComponent('FolderIcon'),
          description: 'Define change request information, requirements, and core details',
          status: isCompleted ? 'completed' : 'in_progress',  // Dynamic status based on completedTasks
          sectionName: 'Analysis',  // Add to Analysis section with User roles, User stories, Process flows
          sectionId: 'analysis-section',
          backgroundColor: '#E8F5E9',  // Green background
          iconColor: '#475969'  // Dark gray icon to match other tiles
        };

        return [changeRequestInfoTile, ...originalFilteredTiles];
      }
    }

    return originalFilteredTiles;
  }, [project?.type, project?.completedTasks, originalFilteredTiles]);

  // Group tiles by section, including the feature-info/change-request-info tile
  // Enhance tiles with validation status
  const tilesWithValidation = useMemo(() => {
    if (!validationSummary || Object.keys(validationSummary).length === 0) return filteredTiles;
    return filteredTiles.map(tile => {
      const valStatus = validationSummary[tile.id];
      if (!valStatus) return tile;
      const enhancedStatus = getTaskStatus(
        tile,
        project?.completedTasks || [],
        conversations,
        valStatus
      );
      return { ...tile, status: enhancedStatus };
    });
  }, [filteredTiles, validationSummary, project, conversations]);

  // Compute task IDs that satisfy dependencies (any status other than 'new' means the task has content)
  const satisfiedTaskIds = useMemo(() => {
    return tilesWithValidation
      .filter(tile => tile.status && tile.status !== 'new')
      .map(tile => tile.id || tile.task_id || tile._id);
  }, [tilesWithValidation]);

  const groupedTiles = useMemo(() => {
    const groups = {};

    tilesWithValidation.forEach(tile => {
      // Normalize section name to fix casing issues (e.g., "Ui/ux" -> "UI/UX")
      const sectionName = normalizeSectionName(tile.sectionName) || tile.sectionName || 'Other';
      if (!groups[sectionName]) {
        groups[sectionName] = [];
      }
      groups[sectionName].push(tile);
    });

    return groups;
  }, [tilesWithValidation]);

  // Custom tile click handler that will either show orchestrator dialog or go directly to conversation
  const handleCustomTileClick = (tile) => {
    
    // If conversations are still loading, show loading state instead of dialog
    if (isLoadingConversations) {
      return;
    }

    // Handle special navigation for MCP and tools integration
    const tileId = tile.id || tile.task_id || tile._id;
    if (tileId === 'agentic-tools-integration' || tileId === 'agentic-mcp-integration') {
      window.open(`/projects/${projectId}/mcp-integration`, '_blank');
      return;
    }

    // Handle TDD Development navigation
    if (tileId === 'tdd-development' || tile.title?.toLowerCase().includes('tdd') || tile.title?.toLowerCase().includes('test-driven')) {
      window.open(`/projects/${projectId}/prd-workflow/edit?step=3&type=tdd-development`, '_blank');
      return;
    }

    // Handle Field Validations — show 3-option dialog unless conversation has messages
    if (tileId === 'field-validations') {
      const existingFVConversation = Array.isArray(conversations) && conversations.find(conv =>
        conv.task_id === 'field-validations' || conv.taskId === 'field-validations'
      );
      // Only skip dialog if conversation exists AND has messages (i.e. actually started)
      const hasMessages = existingFVConversation &&
        existingFVConversation.messages &&
        existingFVConversation.messages.length > 0;
      if (hasMessages) {
        window.open(`/projects/${projectId}/conversation?task=field-validations`, '_blank');
        return;
      }
      setSelectedTaskForFieldValidation(tile);
      setShowFieldValidationDialog(true);
      return;
    }

    // Check if there's already a conversation for this task
    // Handle multiple ID field variations for maximum compatibility
    if (!Array.isArray(conversations) || conversations.length === 0) {
      setSelectedTaskForOrchestrator(tile);
      setShowOrchestratorDialog(true);
      return;
    }
    
    const hasExistingConversation = conversations.some(conv => {
      // Skip conversations without valid task IDs
      if (!conv.taskId && !conv.task_id) {
        return false;
      }
      
      // Get the best available task ID from the tile
      const tileTaskId = tile.id || tile._id || tile.taskId || tile.task_id || tile.sectionId;
      const matches = [
        // conv.taskId === tileTaskId,        // Main tile ID
        conv.task_id === tileTaskId,       // Snake case version
        // conv.taskId === tile.id,           // Standard camelCase
        // conv.task_id === tile.id,          // Snake case (most likely)
        // conv.taskId === tile._id,          // MongoDB ObjectId
        // conv.task_id === tile._id,         // Snake case + ObjectId
        // conv.taskId === tile.taskId,       // Direct taskId match
        // conv.task_id === tile.task_id,     // Direct task_id match
        // conv.taskId === tile.sectionId,    // Tile using sectionId as identifier
        // conv.task_id === tile.sectionId,   // Snake case + sectionId
      ];
      
      const hasMatch = matches.some(match => match === true);
  
      return hasMatch;
    });

    
    
    if (hasExistingConversation) {
      // If a conversation exists, navigate directly to the conversation page
      const taskId = tile.id || tile._id || tile.taskId || tile.task_id || tile.sectionId;

      if (projectId && taskId) {
        const navigationUrl = `/projects/${projectId}/conversation?task=${taskId}`;
        window.open(navigationUrl, '_blank');
      }
    } else {
      // No conversation exists — show the orchestrator dialog
      setSelectedTaskForOrchestrator(tile);
      setShowOrchestratorDialog(true);
    }
  };

  // Set up menu handling
  const {
    anchorEl,
    selectedTile,
    handleMenuOpen,
    handleMenuClose,
    handleTileClick: originalHandleTileClick,  // Renamed to originalHandleTileClick
    handleDownloadPDF,
    handleDownloadDOCX,
    handleEdit
  } = useTaskMenu({
    projectId,
    project,
    showNotification
  });

  // Set up task mutations
  const {
    markTaskAsDone,
    removeTask
  } = useTaskMutations({
    projectId,
    project,
    toggleTaskStatus,
    updateProject,
    getProject: refreshProject,
    setLocalProject,
    showNotification,
    refetchSections
  });

  // Handler to open the task selector dialog
  const handleOpenTaskSelector = useCallback(async (event) => {
    event.stopPropagation();
    event.preventDefault();

    // Before opening the task selector, fetch the latest project data
    if (projectId) {
      try {
        await refreshProject();
      } catch (error) {
      }
    }

    // Call the provided function to open the task selector
    setTimeout(() => {
      if (onOpenTaskSelector) {
        onOpenTaskSelector();
      }
    }, 100); // Small delay to ensure state updates complete
  }, [onOpenTaskSelector, projectId, refreshProject]);

  // Handler for opening foundation conversations
  const handleOpenFoundationConversation = useCallback((tileType) => {
    try {
      const url = `/projects/${projectId}/conversation?task=${tileType}`;
      window.open(url, '_blank');
    } catch (error) {
      showNotification({
        message: `Error opening ${tileType} conversation: ${error.message}`,
        type: 'error'
      });
    }
  }, [projectId, showNotification]);


  return (
    <Box
      ref={containerRef}
      sx={{
        width: '100%',
        pr: 4, // No right padding
        pt: 0,
        pb: 0,
        m: 0,
        height: contentHeight,
        overflowY: 'auto',
        // Custom scrollbar styling
        '&::-webkit-scrollbar': {
          width: '8px',
          backgroundColor: 'transparent',
        },
        '&::-webkit-scrollbar-track': {
          borderRadius: '10px',
          backgroundColor: 'rgba(0, 0, 0, 0.05)',
          marginTop: '5px',
          marginBottom: '5px',
        },
        '&::-webkit-scrollbar-thumb': {
          borderRadius: '10px',
          backgroundColor: 'rgba(0, 0, 0, 0.2)',
          '&:hover': {
            backgroundColor: 'rgba(25, 118, 210, 0.4)',
          },
        },
        '&::-webkit-scrollbar-corner': {
          backgroundColor: 'transparent',
        },
      }}
    >
      {/* Loading state */}
      {(isLoadingSections || isLoadingConversations) && <LoadingView />}

      {/* Error state */}
      {sectionsError && <ErrorView error={sectionsError} onRetry={refetchSections} />}

      {/* Empty state */}
      {!isLoadingSections && !sectionsError && filteredTiles.length === 0 && (
        <EmptyStateView onAddTopics={onOpenTaskSelector ? handleOpenTaskSelector : undefined} />
      )}

      {/* Automated Generation Section - Always show */}
      {!isLoadingSections && !sectionsError && (
        <AutomatedGenerationSection
          projectId={projectId}
          project={project}
          showNotification={showNotification}
          onOpenConversation={handleOpenFoundationConversation}
        />
      )}

      {/* Agentic Crew Workflow - Disabled to show regular layout */}
      {/* {!isLoadingSections && !sectionsError && project?.applicationType === 'agentic-crew' && (
        <AgenticCrewWorkflow
          projectId={projectId}
          project={project}
          onTaskClick={handleCustomTileClick}
          onMenuOpen={handleMenuOpen}
          showNotification={showNotification}
        />
      )} */}

      {/* Tasks organized by section - Show all sections for all project types */}
      {!isLoadingSections && !sectionsError && filteredTiles.length > 0 && (
        <Box sx={{
          width: '100%',
          maxWidth: { xs: '1600px', xl: '1800px' },
          mx: 'auto',
          px: 0
        }}>
          {/* Organize tiles by category/section */}
          {Object.entries(groupedTiles)
            .filter(([sectionName, sectionTiles]) => {
              // For agentic crew projects, only show agentic sections
              if (project?.applicationType === 'agentic-crew') {
                // Show only sections that contain agentic tasks
                const hasAgenticTasks = sectionTiles.some(tile => 
                  tile.id?.startsWith('agentic-') || tile.task_id?.startsWith('agentic-')
                );
                return hasAgenticTasks;
              }
              // For regular projects, exclude agentic sections
              const hasAgenticTasks = sectionTiles.some(tile => 
                tile.id?.startsWith('agentic-') || tile.task_id?.startsWith('agentic-')
              );
              return !hasAgenticTasks;
            })
            .filter(([sectionName, sectionTiles]) => {
              // Hide section if all tiles are permission-denied
              return sectionTiles.some(tile => {
                const permission = getTilePermission(tile);
                if (!permission) return true;
                return hasPermission(permission);
              });
            })
            .map(([sectionName, sectionTiles], sectionIndex) => (
            <React.Fragment key={sectionName}>
              {/* Section as a row with name on left and tasks on right */}
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: { xs: 'column', md: 'row' },
                  gap: { xs: 2, md: 4 },
                  mb: { xs: 2, md: 0 },
                  mt: 0
                }}
              >
                {/* Section header with avatar selection */}
                <SectionHeader
                  sectionName={sectionName}
                  description={getSectionDescription(sectionName)}
                  sectionId={sectionTiles.length > 0 ? sectionTiles[0].sectionId : null}
                  projectId={projectId}
                  // Pass consolidated avatar/orchestrator data to prevent additional API calls
                  // Use section ID for lookup (more reliable than name matching which can break due to normalization)
                  consolidatedAvatar={hasConsolidatedData ? (() => {
                    const sId = sectionTiles.length > 0 ? sectionTiles[0].sectionId : null;
                    const section = sId
                      ? sections.find(s => s.id === sId || s._id === sId)
                      : sections.find(s => s.name === sectionName);
                    return section?.avatar || { url: null, name: null };
                  })() : null}
                  consolidatedOrchestrator={hasConsolidatedData ? (() => {
                    const sId = sectionTiles.length > 0 ? sectionTiles[0].sectionId : null;
                    const section = sId
                      ? sections.find(s => s.id === sId || s._id === sId)
                      : sections.find(s => s.name === sectionName);
                    return section?.orchestrator || { name: null, role: 'Orchestrator', avatar_url: null, assigned: false };
                  })() : null}
                  useConsolidatedData={hasConsolidatedData}
                />

                {/* Tasks grid */}
                <TasksGrid
                  sectionTiles={sectionTiles}
                  onTileClick={handleCustomTileClick}
                  onMenuOpen={handleMenuOpen}
                  onViewHistory={handleTileViewHistory}
                  validationSummary={validationSummary}
                  completedTasks={satisfiedTaskIds}
                />
              </Box>

              {/* Add section divider as a separate element unless it's the last section */}
              {sectionIndex !== Object.keys(groupedTiles).length - 1 && (
                <div className="section-divider" />
              )}
            </React.Fragment>
          ))}
        </Box>
      )}

      {/* Menu for tile actions */}
      <TaskMenu
        anchorEl={anchorEl}
        selectedTile={selectedTile}
        onClose={handleMenuClose}
        onEdit={handleEdit}
        onDownloadPDF={handleDownloadPDF}
        onDownloadDOCX={handleDownloadDOCX}
        onRemove={removeTask}
        showNotification={showNotification}
        isOwnerOrAdmin={canAssignValidators}
        onAssignValidators={handleAssignValidators}
        onViewHistory={handleViewHistory}
      />

      {/* Validation Dialog */}
      <ValidationDialog
        open={showValidationDialog}
        onClose={() => { setShowValidationDialog(false); setValidationTargetTile(null); }}
        taskId={validationTargetTile?.id}
        validatorType={validationTargetTile ? getUserValidatorType(validationTargetTile.id) : null}
        onSubmit={handleSubmitValidation}
        loading={validationLoading}
      />

      {/* Validator Assignment Dialog */}
      <ValidatorAssignmentDialog
        open={showAssignValidatorsDialog}
        onClose={() => { setShowAssignValidatorsDialog(false); setValidationTargetTile(null); setAssignValidatorRestriction(null); }}
        projectId={projectId}
        taskId={validationTargetTile?.id}
        members={projectMembers}
        onUpdate={fetchValidationSummary}
        currentUser={user}
        project={project}
        restrictTo={assignValidatorRestriction}
      />

      {/* Validation History Panel */}
      <ValidationHistoryPanel
        open={showValidationHistory}
        onClose={() => { setShowValidationHistory(false); setTimeout(() => { setValidationTargetTile(null); setHistoryRestriction(null); }, 1000); }}
        projectId={projectId}
        taskId={validationTargetTile?.id}
        restrictTo={historyRestriction}
        fetchKey={historyFetchKey}
        currentUserMemberType={(() => {
          const userId = user?.id || user?._id;
          const member = (projectMembers || []).find(m => String(m.user_id) === String(userId));
          return member?.member_type || 'internal';
        })()}
      />

      {/* Agent Dialog - Only render when we have a valid task */}
      {showOrchestratorDialog && selectedTaskForOrchestrator && (
        <AgentDialog
          open={showOrchestratorDialog}
          onClose={() => setShowOrchestratorDialog(false)}
          task={selectedTaskForOrchestrator}
          user={user}
          projectId={projectId}
        />
      )}

      {/* Field Validation Dialog - 3-option radio selection */}
      {showFieldValidationDialog && selectedTaskForFieldValidation && (
        <FieldValidationDialog
          open={showFieldValidationDialog}
          onClose={() => setShowFieldValidationDialog(false)}
          task={selectedTaskForFieldValidation}
          user={user}
          projectId={projectId}
        />
      )}

      {/* Global Search Dialog (Cmd+K) */}
      <React.Suspense fallback={null}>
        <SearchDialog
          open={searchOpen}
          onClose={() => setSearchOpen(false)}
          projectId={projectId}
        />
      </React.Suspense>
    </Box>
  );
};

Foundation.propTypes = {
  projectId: PropTypes.string.isRequired,
  project: PropTypes.object,
  showNotification: PropTypes.func.isRequired,
  onOpenTaskSelector: PropTypes.func
};

export default Foundation;